/**
 * event-date.js
 */

import React from 'react';
import { 
  useMediaQuery, 
  Box, 
  Divider, 
  Popover, 
  ButtonGroup, 
  Typography 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Alert } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getHours, format, parse, parseISO, addYears, isToday } from 'date-fns';
import { formatDurationHours, dateParamFormat } from 'utils/datetime';
import { renderForMobileContext } from 'utils/app-context';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import Button from 'components/button';
import Icon from 'components/icon';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import TextField from 'components/form-inputs/text-field';


const useStyles = makeStyles(theme => ({
  duration: {
    maxWidth: '150px !important',
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      maxWidth: '200px !important',
    },
    '&.custom-width': {
      maxWidth: 'unset !important',
    },
  },
  durationAdornmentContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    '& .hours': {
      height: '100%',
      backgroundColor: theme.palette.sys.surfaceContainerHigh,
      padding: `0 ${theme.spacing(1)}`,
      fontSize: theme.typography.sizes[2],
    },
    '& .buttons': {
      display: 'flex',
      flexDirection: 'column',
      [theme.size.menuDrawer.mediaRuleMobileContent]: {
        flexDirection: 'row'
      },
      '& > .MuiButtonBase-root': {
        flex: 1,
        padding: 0,
        minWidth: theme.spacing(6),
        maxHeight: 20,
      },
      '& > *:first-child': {
        transform: 'rotate(180deg)',
        top: 2,
        [theme.size.menuDrawer.mediaRuleMobileContent]: {
          top: 0,
        },
      },
      '& > *:last-child': {
        top: -1,
        [theme.size.menuDrawer.mediaRuleMobileContent]: {
          top: 0
        },
      }
    }
  },
  bgButton: {
    fontSize: theme.typography.sizes[5],
  },
  alert: {
    marginTop: theme.spacing(1)
  }
}));


export function EventEditDateFields(props) {
  const {
    storedRecord,
    translation,
    isNameCustomized,
    focusHandlers,
    forceNowDefault,
    stackedFieldWidth,
    rowSpacing,
    maxDate,
    maxDateMessage,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation(translation);
  const { user } = React.useContext(UserContext);
  const { getActiveDogs } = React.useContext(DogsContext);
  const areFieldsStacked = useMediaQuery(theme => theme.size.menuDrawer.mediaRuleMobileContent, { noSsr: true });
  const [durationMenuAnchor, setDurationMenuAnchor] = React.useState();
  const { control, setValue, resetField } = useFormContext();
  const dateValue = useWatch({ control, name: 'date' });
  const durationValue = useWatch({ control, name: 'duration' });
  const setValProps = { shouldDirty: true };
  const noActiveDogsForDate = React.useMemo(() => user.role.isHandler && getActiveDogs(dateValue).length === 0, [user, getActiveDogs, dateValue]);


  // when creating event for today on mobile, use NOW-time
  React.useEffect(() => {
    const dateParam = new URLSearchParams(window.location.search).get('date');
    const dateParamObj = forceNowDefault
      ? new Date()
      : dateParam ? parse(dateParam, dateParamFormat, new Date()) : null;
    if (forceNowDefault || (storedRecord.id === 0 && renderForMobileContext() && isToday(dateParamObj))) {
      // we need to add "NOW-time" to this since it will parse to midnight otherwise
      const date = parseISO(`${format(dateParamObj, 'yyyy-MM-dd')}T${format(new Date(), 'HH:mm:ss')}`);
      resetField('date', { defaultValue: date } );
    }
  }, [storedRecord, resetField, forceNowDefault]);


  // when date/time changes -> update name (unless its been customized already)
  React.useEffect(() => {
    if (!dateValue 
      || typeof dateValue === 'string'
      || (dateValue && dateValue.toString() === 'Invalid Date')
      || isNameCustomized
    ) {
      return;
    } 

    const hour = getHours(dateValue);
    let partOfDay = "";
    if (hour >= 6 && hour < 12) {
      partOfDay = "Morning";
    } else if (hour >= 12 && hour < 17) {
      partOfDay = "Afternoon";
    } else if (hour >= 17 && hour < 22) {
      partOfDay = "Evening";
    } else if (hour >= 0 && hour < 6) {
      partOfDay = "Early Morning";
    } else {
      partOfDay = "Night";
    }

    setValue('name', `${format(dateValue, 'EEEE')} ${partOfDay} Training`);
  }, [dateValue, isNameCustomized, setValue]);


  const incrementDuration = (hours) => {
    const newDuration = durationValue + hours;
    setValue('duration', newDuration || 0.25, setValProps);
  }


  const setDuration = (hours) => {
    setValue('duration', hours, setValProps);
  }


  return (
    <>
      <div data-testid="event-datetime">
        <FormGridContainer rowSpacing={rowSpacing}>

          {/* date time picker */}
          <FormGridItem md={7}>
            <Box sx={{ width: areFieldsStacked ? stackedFieldWidth : undefined }}>
              <DateTimePicker
                variant='datetime'
                name='date'
                label={t('dateTime.dateTime.label')}
                focusHandlers={focusHandlers}
                format="MM/dd/yyyy HH:mm"
                showTodayButton={true}
                rules={{ 
                  required: t('dateTime.dateTime.errMsgEmpty'),
                }}
                maxDate={maxDate || addYears(new Date(), 1)}
                maxDateMessage={maxDateMessage || t('dateTime.dateTime.errMsgMaxDate')}
                minDate={addYears(new Date(), -20)}
                minDateMessage={t('dateTime.dateTime.errMsgMinDate')}
                data-testid='event-datetime-picker'
              />
            </Box>
          </FormGridItem>

          {/* duration */}
          <FormGridItem md={5}>
            <TextField
              name='duration'
              label={t('dateTime.duration.label')}
              className={`${classes.duration}${(areFieldsStacked && stackedFieldWidth) ? ' custom-width' : ''}`}
              style={(areFieldsStacked && stackedFieldWidth) 
                ? { 
                  width: stackedFieldWidth,
                }
                : undefined
              }
              displayValueAs={value => formatDurationHours(value)}
              focusHandlers={focusHandlers}
              onClick={e => setDurationMenuAnchor(e.currentTarget)}
              onKeyDown={e => {
                e.stopPropagation();
                e.preventDefault(); 
                setDurationMenuAnchor(e.currentTarget); 
              }}
              InputProps={{
                endAdornment: <div className={`flex-hc ${classes.durationAdornmentContainer}`}>
                  <div className='flex-hc hours'>
                    <Icon type='date' />
                  </div>
                  <div className='buttons'>
                    <Button>
                      <Icon
                        type='expander'
                        onClick={e => {
                          e.stopPropagation();
                          incrementDuration(0.25);
                        }}
                      />
                      </Button>
                    <Divider />
                    <Button>
                      <Icon
                        type='expander'
                        onClick={e => {
                          e.stopPropagation();
                          incrementDuration(-0.25);
                        }}
                      />
                    </Button>
                  </div>
                </div>
              }}
            />
          </FormGridItem>
        </FormGridContainer>

        {noActiveDogsForDate &&
          <Alert severity='warning' className={classes.alert}>
            <Typography>
              You don't have any dogs configured for this event date and won't be able to complete any exercises.
              To fix this, modify the event date to match one of your active dogs or modify the Record Keeping Start Date and/or End Date in your Dog settings to match this training event.
            </Typography>
          </Alert>}

      </div>

      <Popover
        open={Boolean(durationMenuAnchor)}
        anchorEl={durationMenuAnchor}
        onClose={() => setDurationMenuAnchor()}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
      >
        <ButtonGroup
          aria-label="outlined secondary button group"
          disableElevation
          className={classes.buttonGroup}
        >
          {[1,2,3,4,6,8].map(hours =>
            <Button
              key={hours}
              className={classes.bgButton}
              onClick={() => {
                setDuration(hours);
                setDurationMenuAnchor();
              }}
            >
              {hours}
            </Button>)}
        </ButtonGroup>
      </Popover>
    </>
  );
}


export default function EventEditDate(props) {
  const { translation, drawerState } = props;
  const { t } = useTranslation(translation);

  return (
    <EditorIconSection
      iconName='event'
      helpProps={{
        translation,
        section: 'dateTime'
      }}
    >
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('dateTime.dateTime.helpTitle')}
        text={t('dateTime.dateTime.helpInfo')}
      >
        {focusHandlers =>
          <EventEditDateFields {...props} focusHandlers={focusHandlers} />
        }
      </HelpTextItem>
    </EditorIconSection>
  );
}